import { useState } from "react";
import { Outlet, NavLink } from "react-router-dom";
import { TabProvider } from "@/contexts/TabContext";
import { TabBar } from "@/components/tabs/TabBar";
import { TabRenderer } from "@/components/tabs/TabRenderer";
import { TabNavLink } from "@/components/tabs/TabNavLink";
import { platformTabRegistry } from "@/tabs/platformTabRegistry";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminRole } from "@/hooks/useAdminRole";
import { useOpenTicketCount } from "@/hooks/useSupportTickets";
import { useOpenIncidentCount } from "@/hooks/useTenantIncidents";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard, Building2, TrendingUp, MessageSquareText,
  Users, LogOut, Crown, AlertTriangle, Bot,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { CopilotDrawer } from "@/components/platform/copilot/CopilotDrawer";

const navItems = [
  { to: "/platform", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/platform/incidents", label: "Incidents", icon: AlertTriangle },
  { to: "/platform/billing", label: "MRR & Billing", icon: TrendingUp },
  { to: "/platform/support", label: "Support", icon: MessageSquareText },
  { to: "/platform/team", label: "Admin Team", icon: Users },
];

export const PlatformLayout = () => {
  const { user, signOut } = useAuth();
  const { role } = useAdminRole();
  const openTicketCount = useOpenTicketCount();
  const openIncidentCount = useOpenIncidentCount();
  const [copilotOpen, setCopilotOpen] = useState(false);
  const initials = user?.email ? user.email.substring(0, 2).toUpperCase() : "PA";

  return (
    <TabProvider registry={platformTabRegistry}>
    <div className="flex min-h-screen">
      {/* Sidebar */}
      <aside className="hidden w-56 shrink-0 border-r bg-card md:flex md:flex-col">
        <div className="p-4 border-b">
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-primary/10 p-2">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold">Platform Admin</p>
              <div className="flex items-center gap-1">
                <Crown className="h-3 w-3 text-primary" />
                <span className="text-[10px] text-muted-foreground capitalize">{role ?? "admin"}</span>
              </div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-2 py-4 space-y-1">
          {navItems.map((item) => (
            <TabNavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                )
              }
            >
              {() => (
                <>
                  <item.icon className="h-4 w-4" />
                  {item.label}
                  {item.to === "/platform/support" && openTicketCount > 0 && (
                    <Badge variant="destructive" className="ml-auto text-[10px] px-1.5 py-0 animate-pulse">
                      {openTicketCount}
                    </Badge>
                  )}
                  {item.to === "/platform/incidents" && openIncidentCount > 0 && (
                    <Badge variant="destructive" className="ml-auto text-[10px] px-1.5 py-0 animate-pulse">
                      {openIncidentCount}
                    </Badge>
                  )}
                </>
              )}
            </TabNavLink>
          ))}

          {/* Copilot trigger */}
          <button
            onClick={() => setCopilotOpen(true)}
            className={cn(
              "flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors w-full",
              "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            )}
          >
            <Bot className="h-4 w-4" />
            Copilot
          </button>
        </nav>

        <div className="border-t p-3 space-y-2">
          <div className="flex items-center gap-2 px-1">
            <Avatar className="h-7 w-7">
              <AvatarFallback className="text-[10px] bg-primary text-primary-foreground">{initials}</AvatarFallback>
            </Avatar>
            <p className="text-xs text-muted-foreground truncate flex-1">{user?.email}</p>
          </div>
          <Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-xs" onClick={() => signOut()}>
            <LogOut className="h-3.5 w-3.5" /> Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex items-center justify-between border-b p-3 md:hidden">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            <span className="font-semibold text-sm">Platform Admin</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => signOut()}>
            <LogOut className="h-4 w-4" />
          </Button>
        </header>

        {/* Mobile bottom nav */}
        <nav className="fixed inset-x-0 bottom-0 z-50 flex items-center justify-around border-t bg-card/95 backdrop-blur-sm py-1 md:hidden safe-area-pb">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "flex flex-col items-center gap-0.5 px-2 py-1.5 text-[10px] font-medium transition-colors rounded-md relative",
                  isActive ? "text-primary" : "text-muted-foreground"
                )
              }
            >
              <item.icon className="h-5 w-5" />
              {item.label}
              {item.to === "/platform/support" && openTicketCount > 0 && (
                <span className="absolute -top-0.5 right-0 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[8px] text-destructive-foreground">
                  {openTicketCount}
                </span>
              )}
              {item.to === "/platform/incidents" && openIncidentCount > 0 && (
                <span className="absolute -top-0.5 right-0 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[8px] text-destructive-foreground">
                  {openIncidentCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Desktop tab bar */}
        <div className="hidden md:block">
          <TabBar />
        </div>

        {/* Mobile: traditional Outlet */}
        <main className="flex-1 overflow-auto p-2 pb-20 md:hidden">
          <Outlet />
        </main>

        {/* Desktop: persistent tab panels */}
        <div className="hidden md:flex md:flex-1 md:overflow-hidden">
          <div className="flex-1 overflow-auto p-4">
            <TabRenderer />
          </div>
        </div>
      </div>

      <CopilotDrawer open={copilotOpen} onOpenChange={setCopilotOpen} />
    </div>
    </TabProvider>
  );
};